import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Container,
    TextField,
    Button,
    InputAdornment,
    Alert,
    CircularProgress,
    Stack,
} from '@mui/material';
import {
    Email as EmailIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { newsletterApi } from '../../services/api';

const NewsletterSection = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null); // { type: 'success' | 'error', message: string } | null
    const [subscriberCount, setSubscriberCount] = useState(null);

    useEffect(() => {
        newsletterApi.getCount()
            .then(data => setSubscriberCount(data.count))
            .catch(() => { }); // silent fail — social proof is non-critical
    }, []);

    const handleSubscribe = async (e) => {
        e.preventDefault();

        // Basic frontend validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            setStatus({ type: 'error', message: 'Please enter a valid email address.' });
            return;
        }

        setLoading(true);
        setStatus(null);
        try {
            const response = await newsletterApi.subscribe(email, {
                source: 'landing_page_waitlist',
                signup_type: 'waitlist',
                consentGiven: true, // Mandatory for waitlist entry
            });

            if (response.alreadySubscribed) {
                setStatus({
                    type: 'success',
                    message: "You're already on our waitlist! We'll reach out with your invite soon."
                });
            } else {
                setStatus({
                    type: 'success',
                    message: "You're in! We've sent a confirmation email to your inbox."
                });
            }
            setEmail('');
        } catch (err) {
            const is429 = err.response?.status === 429;
            setStatus({
                type: 'error',
                message: is429
                    ? 'Too many attempts. Please wait a few minutes and try again.'
                    : err.message || 'Something went wrong. Please try again later.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box id="waitlist" sx={{ py: { xs: 10, md: 16 }, position: 'relative', overflow: 'hidden' }}>
            {/* Background Accents */}
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '80vw', height: '40vh', background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' }} />

            <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{
                    p: { xs: 4, md: 8 },
                    borderRadius: 6,
                    bgcolor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.07)',
                    backdropFilter: 'blur(16px)',
                    textAlign: 'center',
                    boxShadow: '0 40px 100px rgba(0,0,0,0.4)',
                }}>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: '#6366f1', letterSpacing: '0.2em', fontSize: '0.8rem' }}>
                        MARKET VERIFICATION & WAITLIST
                    </Typography>
                    <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2.5rem', md: '3.8rem' }, letterSpacing: '-0.04em', mt: 2, mb: 3 }}>
                        Ready to automate your growth?
                    </Typography>

                    {subscriberCount > 5 && (
                        <Typography variant="body2" sx={{ color: '#10d98a', fontWeight: 800, mb: 3, letterSpacing: '0.05em' }}>
                            JOIN {subscriberCount.toLocaleString()}+ OTHERS ALREADY ON THE LIST
                        </Typography>
                    )}

                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.2rem', mb: 6, maxWidth: 540, mx: 'auto', lineHeight: 1.6 }}>
                        Join the restricted waitlist for the Socialixer. We're launching in batches to ensure every user gets high-performance AI attention.
                    </Typography>

                    {status?.type === 'success' ? (
                        <Box sx={{
                            py: 4, px: 3, borderRadius: 3, bgcolor: 'rgba(16, 217, 138, 0.08)', border: '1px solid rgba(16, 217, 138, 0.2)',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, animation: 'fadeIn 0.5s ease-out'
                        }}>
                            <CheckCircleIcon sx={{ color: '#10d98a', fontSize: 48 }} />
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 800 }}>Success!</Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 380 }}>{status.message}</Typography>
                            <Button
                                onClick={() => setStatus(null)}
                                sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none', mt: 1, '&:hover': { color: 'white' } }}
                            >
                                Subscribed? Close this.
                            </Button>
                        </Box>
                    ) : (
                        <Box component="form" onSubmit={handleSubscribe} sx={{ maxWidth: 500, mx: 'auto' }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Enter your email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: 'rgba(255,255,255,0.03)',
                                            borderRadius: '100px',
                                            height: 60,
                                            px: 1,
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            transition: 'all 0.3s',
                                            '&:hover': { border: '1px solid rgba(255,255,255,0.2)' },
                                            '&.Mui-focused': { border: '1px solid #6366f1', boxShadow: '0 0 20px rgba(99, 102, 241, 0.2)' },
                                        },
                                        '& input': { color: 'white', fontWeight: 600, px: 2 },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon sx={{ color: 'rgba(255,255,255,0.3)', ml: 1 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    sx={{
                                        minWidth: { xs: '100%', sm: 160 },
                                        height: 60,
                                        borderRadius: '100px',
                                        bgcolor: '#6366f1',
                                        color: 'white',
                                        fontWeight: 800,
                                        fontSize: '1rem',
                                        boxShadow: '0 10px 20px -5px rgba(99, 102, 241, 0.4)',
                                        '&:hover': { bgcolor: '#818cf8', transform: 'translateY(-2px)' },
                                        transition: 'all 0.3s'
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Get Early Access'}
                                </Button>
                            </Stack>

                            {status?.type === 'error' && (
                                <Alert
                                    severity="error"
                                    onClose={() => setStatus(null)}
                                    sx={{ mt: 3, borderRadius: 3, bgcolor: 'rgba(244, 67, 54, 0.08)', color: '#ff8a80', border: '1px solid rgba(244, 67, 54, 0.2)', '& .MuiAlert-icon': { color: '#ff8a80' } }}
                                >
                                    {status.message}
                                </Alert>
                            )}

                            <Typography variant="caption" sx={{ display: 'block', mt: 3, color: 'rgba(255,255,255,0.3)', fontWeight: 500, lineHeight: 1.5 }}>
                                By joining the waitlist, you agree to receive product updates from Socialixer.
                                We value your privacy and you can unsubscribe at any time.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Container>

            <style>
                {`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                `}
            </style>
        </Box>
    );
};

export default NewsletterSection;
